import React from "react";
import { TemplatePartnerPortalIndexV2 } from "./template_partner_portal_index_v2";
import { PartnerConfig } from "../../partner_config";
import { Partner } from "../../api";
import { Spacer } from "../../spacer";
import styles from "./portal_membership_page.module.css";
import { useMediaQuery } from "@/hooks";
import { PortalBookingList } from "../components/portal_booking_list";
import { PortalMembershipSpaceList } from "../components/portal_membership_space_list";
import {
  PortalPathParams,
  usePortalHistory,
} from "../hooks/use_portal_history";
import { PortalBackButton } from "../components/portal_back_button";
import { useSubscriptionDetailsQuery } from "../hooks/use_subscription_details_query";
import { useParams } from "react-router-dom";
import { PortalButton } from "../components/portal_button";
import { usePortalManageMembership } from "@/core/splitio";
import { formatCurrency } from "@/lib/currency/currency";
import { useUpcomingPaymentQuery } from "../hooks/use_upcoming_payment_query";
import { formatDate } from "@/offering/date_utils";
import { colors } from "@/offering_v2/components/colors";
import { BsInfoCircleFill } from "react-icons/bs";
import classNames from "classnames";
import { MembershipFailedPaymentBar } from "../components/membership_failed_payment_bar";
import { useMembershipManagementAnalytics } from "../hooks/use_membership_management_analytics";
import { useSubscriptionOfferingsQuery } from "../hooks/use_subscription_offerings_query";

interface PortalMembershipPageProps {
  partnerConfig: PartnerConfig;
  partner: Partner;
}

export function PortalMembershipPage({
  partnerConfig,
  partner,
}: PortalMembershipPageProps) {
  const { device } = useMediaQuery();
  const { toHome, toManageMembershipPage } = usePortalHistory();
  const { subscriptionId } = useParams<PortalPathParams>();

  const analyticEvent = useMembershipManagementAnalytics();
  const { data: subscriptionDetails } = useSubscriptionDetailsQuery(
    partner.id,
    subscriptionId
  );

  const { data: offerings } = useSubscriptionOfferingsQuery(
    partner.id,
    subscriptionId
  );

  const { data: upcomingPaymentData } = useUpcomingPaymentQuery(
    partner.id,
    subscriptionId
  );
  const portalManageMembershipFlag = usePortalManageMembership(partner.id);
  const isPendingCancel = subscriptionDetails?.status === "pending_cancel";
  const isCancelled = subscriptionDetails?.status === "canceled";

  const handleClickManangeMembership = () => {
    analyticEvent("Membership Details Click");
    toManageMembershipPage();
  };

  return (
    <TemplatePartnerPortalIndexV2
      partner={partner}
      partnerConfig={partnerConfig}
    >
      <div>
        <Spacer size={device === "mobile" ? 38 : 45} />
        <PortalBackButton onClick={toHome}>
          Back to Membership Portal
        </PortalBackButton>
        <Spacer size={device === "mobile" ? 38 : 45} />

        {subscriptionDetails?.paymentStatus === "payment_failed" &&
          !isCancelled && (
            <div className="mb-3">
              <MembershipFailedPaymentBar
                partnerId={partner.id}
                partnerSlug={partner.partnerSlug}
                partnerName={partner.name}
              />
            </div>
          )}

        {isPendingCancel &&
        upcomingPaymentData &&
        subscriptionDetails.endDate ? (
          <div
            className={classNames(
              device !== "mobile" && "d-flex gap-3 align-items-center",
              "p-4 rounded mb-3"
            )}
            style={{ background: colors.bananaMinus70, color: colors.banana60 }}
          >
            {device !== "mobile" && (
              <div>
                <BsInfoCircleFill size={32} />
              </div>
            )}
            <div>
              <b>Your membership has been cancelled.</b> Your next payment of{" "}
              <b>
                {formatCurrency(
                  upcomingPaymentData.totalPrice,
                  upcomingPaymentData.currency
                )}
              </b>{" "}
              will be processed on{" "}
              <b>{formatDate(upcomingPaymentData.paymentDate)}</b>, and access
              to your membership ends on{" "}
              <b>{formatDate(subscriptionDetails.endDate)}</b>.
            </div>
          </div>
        ) : isCancelled ? (
          <div
            className={classNames(
              device !== "mobile" && "fs-3",
              "p-4 rounded mb-3"
            )}
            style={{
              background: colors.grapefruitMinus70,
              color: colors.grapefruit60,
            }}
          >
            <b>Your membership has been cancelled.</b>
          </div>
        ) : null}

        <img
          className={styles.img}
          src={subscriptionDetails?.membership.imgUrl}
          alt={subscriptionDetails?.membership.name}
        />
        <Spacer size={device === "mobile" ? 32 : 40} />
        <div className={styles.titleWrapper}>
          <div>
            <div className={styles.title}>
              {subscriptionDetails?.membership.name}
            </div>
            {upcomingPaymentData && (
              <div className="mt-3">
                Your {isPendingCancel ? "last" : "next"} bill is for{" "}
                <b>
                  {formatCurrency(
                    upcomingPaymentData.totalPrice,
                    upcomingPaymentData.currency
                  )}
                </b>{" "}
                on <b>{formatDate(upcomingPaymentData.paymentDate)}</b>
              </div>
            )}
          </div>

          {portalManageMembershipFlag && (
            <div>
              {device === "mobile" && <Spacer size={16} />}
              <PortalButton
                appearance="outlined"
                onClick={handleClickManangeMembership}
              >
                {isCancelled ? "Membership details" : "Manage membership"}
              </PortalButton>
            </div>
          )}
        </div>

        {!isCancelled && (
          <div>
            <Spacer size={device === "mobile" ? 32 : 40} />
            <div>
              <PortalMembershipSpaceList
                partnerSlug={partner.partnerSlug}
                partnerId={partner.id}
              />
            </div>
          </div>
        )}

        {/* Hide perks */}
        {/* <Spacer size={device === "mobile" ? 32 : 40} />
        <PortalYourPerks perks={FAKE_MEMBERSHIP_DETAILS.perks} /> */}

        {offerings && offerings.length > 0 && (
          <div>
            <Spacer size={device === "mobile" ? 16 : 24} />
            <PortalBookingList
              partnerId={partner.id}
              bookingType="membership"
            />
          </div>
        )}
        <Spacer size={device === "mobile" ? 16 : 24} />
        <div className="text-end">
          Cancelling your membership? Click on 'Manage Membership'.
        </div>
      </div>
    </TemplatePartnerPortalIndexV2>
  );
}
